import { FC } from 'react';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ITrainingCategory } from '../../../models/allenamenti';
import CategoryRow from './CategoryRow';
import { ICategoryFormDraft } from './useCategoriesModal';
import { sortableStyle } from './sortableStyle';
import styles from './CategoryGroup.module.scss';

interface ICategoryGroupProps {
  category: ITrainingCategory;
  isOpen: boolean;
  onToggle: () => void;
  isSaving: boolean;
  pendingDelete: string | null;
  onStartCreate: (parentSlug: string | null, inheritedColor?: string) => void;
  onStartEdit: (
    slug: string,
    parentSlug: string | null,
    values: ICategoryFormDraft,
  ) => void;
  onAskDelete: (slug: string | null) => void;
  onConfirmDelete: (slug: string) => void;
}

/**
 * Riga di sottocategoria trascinabile.
 *
 * Componente a sé perché `useSortable` è un hook: chiamarlo dentro il `.map()`
 * del genitore violerebbe le regole degli hook.
 */
const SortableSub: FC<{
  sub: ITrainingCategory['subcategories'][number];
  parent: ITrainingCategory;
  isSaving: boolean;
  pendingDelete: string | null;
  onStartEdit: ICategoryGroupProps['onStartEdit'];
  onAskDelete: ICategoryGroupProps['onAskDelete'];
  onConfirmDelete: ICategoryGroupProps['onConfirmDelete'];
}> = ({
  sub,
  parent,
  isSaving,
  pendingDelete,
  onStartEdit,
  onAskDelete,
  onConfirmDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sub.slug });

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle(transform, transition, isDragging)}
    >
      <CategoryRow
        label={sub.labelIt}
        isMacro={false}
        /* Il flag della SOTTO, non della macro: le proprie stanno dentro le
           macro di sistema, perché di sistema sono tutte. */
        isSystem={sub.isSystem}
        canModify={!sub.isSystem}
        dragHandle={{ ...listeners, ...attributes }}
        isDragging={isDragging}
        isPendingDelete={pendingDelete === sub.slug}
        isSaving={isSaving}
        onEdit={() =>
          onStartEdit(sub.slug, parent.slug, {
            labelIt: sub.labelIt,
            color: parent.color,
            isPhysical: sub.isPhysical,
            fieldsEnabled: [],
          })
        }
        onAskDelete={() => onAskDelete(sub.slug)}
        onCancelDelete={() => onAskDelete(null)}
        onConfirmDelete={() => onConfirmDelete(sub.slug)}
      />
    </div>
  );
};

/**
 * Una macro con le sue sottocategorie, richiudibile.
 *
 * Chiusa di default: le macro sono dodici e stanno in una schermata, aperte
 * tutte sono ventisei righe. Il conteggio sulla testata dice che dentro c'è
 * qualcosa senza costringere ad aprire.
 *
 * La barra di colore corre lungo tutto il gruppo: è il colore alla scala a cui
 * si vede nel calendario, e dice che quelle sotto appartengono a QUESTA macro.
 *
 * Creazione e modifica NON stanno più qui: aprono una modal propria
 * (`CategoryFormModal`). Prima erano una striscia che compariva dentro
 * l'elenco, spingeva le righe e non somigliava a nessun altro form del
 * progetto.
 */
const CategoryGroup: FC<ICategoryGroupProps> = ({
  category,
  isOpen,
  onToggle,
  isSaving,
  pendingDelete,
  onStartCreate,
  onStartEdit,
  onAskDelete,
  onConfirmDelete,
}) => {
  const subs = category.subcategories;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.slug });

  return (
    <li
      ref={setNodeRef}
      className={styles.group}
      style={sortableStyle(transform, transition, isDragging)}
    >
      {/* Il colore è un dato della categoria: non esiste una classe per un
          valore libero, quindi qui l'inline style è contenuto, non stile. */}
      <span
        className={styles.bar}
        style={{ background: category.color }}
        aria-hidden
      />

      <div className={styles.content}>
        <CategoryRow
          label={category.labelIt}
          isMacro
          isSystem={category.isSystem}
          canModify={!category.isSystem}
          childCount={subs.length}
          isOpen={isOpen}
          onToggleOpen={subs.length ? onToggle : undefined}
          dragHandle={{ ...listeners, ...attributes }}
          isDragging={isDragging}
          isPendingDelete={pendingDelete === category.slug}
          isSaving={isSaving}
          onAddChild={() => onStartCreate(category.slug, category.color)}
          onEdit={() =>
            onStartEdit(category.slug, null, {
              labelIt: category.labelIt,
              color: category.color,
              isPhysical: category.isPhysical,
              fieldsEnabled: category.fieldsEnabled ?? [],
            })
          }
          onAskDelete={() => onAskDelete(category.slug)}
          onCancelDelete={() => onAskDelete(null)}
          onConfirmDelete={() => onConfirmDelete(category.slug)}
        />

        {/* Le sotto rientrano rispetto alla macro: la barra dice a CHI
            appartengono, il rientro dice che stanno DENTRO. */}
        {isOpen && (
          <div className={styles.sub}>
            {/* Le sotto si riordinano fra loro, dentro il loro gruppo: un
                contesto per macro dice a dnd-kit quali righe competono. */}
            <SortableContext
              items={subs.map((x) => x.slug)}
              strategy={verticalListSortingStrategy}
            >
              {subs.map((sub) => (
                <SortableSub
                  key={sub.slug}
                  sub={sub}
                  parent={category}
                  isSaving={isSaving}
                  pendingDelete={pendingDelete}
                  onStartEdit={onStartEdit}
                  onAskDelete={onAskDelete}
                  onConfirmDelete={onConfirmDelete}
                />
              ))}
            </SortableContext>
          </div>
        )}
      </div>
    </li>
  );
};

export default CategoryGroup;
